import React, { useState } from 'react';
import * as RadioGroup from '@radix-ui/react-radio-group';
import {
    DndContext,
    closestCenter,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors,
    DragOverlay,
} from '@dnd-kit/core';
import {
    arrayMove,
    SortableContext,
    sortableKeyboardCoordinates,
    horizontalListSortingStrategy,
} from '@dnd-kit/sortable';
import { restrictToHorizontalAxis } from '@dnd-kit/modifiers';
import { ModuleBlock } from './ModuleBlock';
import { SortableModuleBlock } from './SortableModuleBlock';
import * as AlertDialog from '@radix-ui/react-alert-dialog';


const MODULES = [
    {
        id: 'clinical-interview',
        title: 'Clinical Interview',
        isLocked: false,
    },
    {
        id: 'semi-structured-interview',
        title: 'Semi-Structured Interview',
        isLocked: false,
    },
    {
        id: 'diagnostic-decision',
        title: 'Diagnostic Decision',
        isLocked: true,
    },
    {
        id: 'post-assessment-discussion',
        title: 'Post-Assessment Discussion',
        isLocked: true,
    },
];

const WORKFLOW_OPTIONS = [
    {
        id: 'single-session',
        title: 'Option 1: Single Session Model',
        description: 'The Assessment is completed in one session by a single clinician.',
    },
    {
        id: 'multi-session',
        title: 'Option 2: Multi-session Model',
        description: 'The Assessment is completed over 2 or more sessions.',
    },
    {
        id: 'mdt-integrated',
        title: 'Option 3: MDT-Integrated Model',
        description: 'The Diagnostic Decision is taken in an MDT meeting, outside of patient facing sessions.',
    },
];

const WORKFLOW_STEPS = {
    ORDER: '1',
    SESSION_1: '2',
    SESSION_2: '3',
    SESSION_3: '4',
    SESSION_4: '5',
};

const SESSION_STYLES = {
    1: { border: 'border-red-500', bg: 'bg-red-50' },
    2: { border: 'border-blue-500', bg: 'bg-blue-50' },
    3: { border: 'border-green-500', bg: 'bg-green-50' },
    4: { border: 'border-amber-500', bg: 'bg-amber-50' },
};

export default function ModuleConfiguration() {
    const [selectedOption, setSelectedOption] = useState(null);
    const [pendingOption, setPendingOption] = useState(null);
    const [showResetAlert, setShowResetAlert] = useState(false);
      
    const [currentStep, setCurrentStep] = useState(WORKFLOW_STEPS.ORDER);
    const [modules, setModules] = useState(MODULES);
    const [activeId, setActiveId] = useState(null);
    const [sessionGroups, setSessionGroups] = useState({
        1: [],
        2: [],
        3: [],
        4: [],
    });
    const [selectedModules, setSelectedModules] = useState([]);

    const [isComplete, setIsComplete] = useState(false);

    // Reset function to clear all progress
  const resetConfiguration = () => {
    setCurrentStep(WORKFLOW_STEPS.ORDER);
    setModules(MODULES);
    setActiveId(null);
    setSessionGroups({
      1: [],
      2: [],
      3: [],
      4: [],
    });
    setSelectedModules([]);
    setIsComplete(false);
  };

  // Handle option change with confirmation
  const handleOptionChange = (newOption) => {
    if (selectedOption && (
      currentStep !== WORKFLOW_STEPS.ORDER || 
      Object.values(sessionGroups).some(group => group.length > 0)
    )) {
      setPendingOption(newOption);
      setShowResetAlert(true);
    } else {
      setSelectedOption(newOption);
      resetConfiguration();
    }
  };

    // Function to check if all modules are assigned
  const areAllModulesAssigned = () => {
    const assignedModules = Object.values(sessionGroups)
      .flat()
      .concat(selectedModules);
    return assignedModules.length === modules.length;
  };

    const sensors = useSensors(
        useSensor(PointerSensor),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        })
    );

    const handleDragStart = (event) => {
        const { active } = event;
        setActiveId(active.id);
    };

    const handleDragEnd = (event) => {
        const { active, over } = event;

        if (over && active.id !== over.id) {
            setModules((items) => {
                const oldIndex = items.findIndex((item) => item.id === active.id);
                const newIndex = items.findIndex((item) => item.id === over.id);

                // Only allow reordering if neither module is locked
                if (!items[oldIndex].isLocked && !items[newIndex].isLocked) {
                    return arrayMove(items, oldIndex, newIndex);
                }
                return items;
            });
        }
        setActiveId(null);
    };   

    // Function to get step instructions
    const getStepInstructions = () => {
        if (selectedOption === 'single-session') {
          if (!isComplete) {
            return "Drag and drop the Assessment Blocks into the order that matches your clinical approach.";
          }
          return null; // Will be replaced by completion message
        }
    
        // Existing multi-session instructions
        switch (currentStep) {
          case WORKFLOW_STEPS.ORDER:
                return "Drag and drop the Assessment Blocks into the order that matches your clinical approach.";
            case WORKFLOW_STEPS.SESSION_1:
                return "Select the Assessment Blocks that take place in the first session.";
            case WORKFLOW_STEPS.SESSION_2:
                return "Select the Assessment Blocks that take place in the second session.";
            case WORKFLOW_STEPS.SESSION_3:
                return "Select the Assessment Blocks that take place in the third session.";
            case WORKFLOW_STEPS.SESSION_4:
                return "Select the Assessment Blocks that take place in the fourth session.";
            default:
                return "";
        }
    };

    // Function to check if a module can be selected based on consecutiveness
  const canSelectModule = (moduleId) => {
    // First check if module is already assigned to a session
    if (isModuleAssigned(moduleId)) return false;

    if (selectedModules.length === 0) return true;
    
    const moduleIndex = modules.findIndex(m => m.id === moduleId);
    const selectedIndices = selectedModules.map(id =>
      modules.findIndex(m => m.id === id)
    );
    
    const minSelected = Math.min(...selectedIndices);
    const maxSelected = Math.max(...selectedIndices);
    
    // Check if the new module would be consecutive with currently selected modules
    // AND ensure we're not trying to select across any already-assigned modules
    const isConsecutive = moduleIndex === minSelected - 1 || moduleIndex === maxSelected + 1;
    
    if (!isConsecutive) return false;

    // If selecting before current selection, check if any modules in between are assigned
    if (moduleIndex < minSelected) {
      for (let i = moduleIndex + 1; i < minSelected; i++) {
        if (isModuleAssigned(modules[i].id)) return false;
      }
    }
    // If selecting after current selection, check if any modules in between are assigned
    else if (moduleIndex > maxSelected) {
      for (let i = maxSelected + 1; i < moduleIndex; i++) {
        if (isModuleAssigned(modules[i].id)) return false;
      }
    }

    return true;
  };

    // Modified module click handler
  const handleModuleClick = (moduleId) => {
    if (currentStep === WORKFLOW_STEPS.ORDER) return;
    
    if (selectedModules.includes(moduleId)) {
      // Allow deselection only if it's at the end of the selection
      const moduleIndex = modules.findIndex(m => m.id === moduleId);
      const selectedIndices = selectedModules.map(id =>
        modules.findIndex(m => m.id === id)
      );
      const isAtEnd = moduleIndex === Math.min(...selectedIndices) ||
                     moduleIndex === Math.max(...selectedIndices);
      
      if (isAtEnd) {
        setSelectedModules(prev => prev.filter(id => id !== moduleId));
      }
    } else if (canSelectModule(moduleId)) {
      setSelectedModules(prev => [...prev, moduleId]);
    }
  };
    // Helper function to check if a module is already assigned to a session
  const isModuleAssigned = (moduleId) => {
    return Object.values(sessionGroups).some(group => group.includes(moduleId));
  };

    // Function to check if a module should be locked during ordering
    const isModuleOrderLocked = (module) => {
        return currentStep === WORKFLOW_STEPS.ORDER &&
            (module.id === 'diagnostic-decision' || module.id === 'post-assessment-discussion');
    };

    // Modified confirm step handler
  const handleConfirmStep = () => {
    if (selectedOption === 'single-session') {
        // For Option 1, assign all modules to session 1 and complete
        setSessionGroups(prev => ({
          ...prev,
          1: modules.map(m => m.id),
          2: [],
          3: [],
          4: [],
        }));
        setIsComplete(true);
      } else {
        // Existing multi-session logic
        if (currentStep === WORKFLOW_STEPS.ORDER) {
          setCurrentStep(WORKFLOW_STEPS.SESSION_1);
          return;
        }

    setSessionGroups(prev => ({
      ...prev,
      [currentStep]: selectedModules
    }));

    // Clear selection
    setSelectedModules([]);

    // Check if all modules are now assigned
    const willBeComplete = areAllModulesAssigned();
    if (willBeComplete) {
      setIsComplete(true);
    } else {
      // Determine next step
      switch (currentStep) {
        case WORKFLOW_STEPS.SESSION_1:
          setCurrentStep(WORKFLOW_STEPS.SESSION_2);
          break;
        case WORKFLOW_STEPS.SESSION_2:
          setCurrentStep(WORKFLOW_STEPS.SESSION_3);
          break;
        case WORKFLOW_STEPS.SESSION_3:
          setCurrentStep(WORKFLOW_STEPS.SESSION_4);
          break;
        default:
          break;
      }
    }
    }
};

    return (
        <div className="space-y-8">
            {/* Top Section - Instructions and Options */}
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
                {/* Left Side - Instructions */}
                <div className="text-left lg:col-span-5 space-y-6">
                    <div className="prose">
                        <h3 className="text-lg font-medium text-gray-900 mb-4">
                            The ThinkDivergent Platform organises Adult ADHD Assessments into four modules or 'Assessment Blocks'
                        </h3>
                        <p className="text-sm text-gray-600">
                            Tailor the Assessment Workflow to suit your clinical approach by selecting one of these options:
                        </p>
                    </div>
                </div>

                {/* Right Side - Options */}
                <div className="lg:col-span-7">
                    <RadioGroup.Root
                        value={selectedOption}
                        onValueChange={handleOptionChange}
                        className="space-y-4"
                    >
                        {WORKFLOW_OPTIONS.map((option) => (
                            <div key={option.id} className="relative">
                                <RadioGroup.Item
                                    value={option.id}
                                    className={`w-full text-left p-4 rounded-lg border-2 transition-all duration-200 ${selectedOption === option.id
                                        ? 'border-violet-600 bg-violet-50'
                                        : 'border-gray-200 hover:border-violet-300'
                                        }`}
                                >
                                    <div className="flex items-start">
                                        <div className="flex h-5 items-center">
                                            <div
                                                className={`h-4 w-4 rounded-full border flex items-center justify-center ${selectedOption === option.id
                                                    ? 'border-violet-600'
                                                    : 'border-gray-300'
                                                    }`}
                                            >
                                                {selectedOption === option.id && (
                                                    <div className="h-2 w-2 rounded-full bg-violet-600" />
                                                )}
                                            </div>
                                        </div>
                                        <div className="ml-3">
                                            <h4 className="text-sm font-medium text-gray-900">
                                                {option.title}
                                            </h4>
                                            <p className="text-sm text-gray-500">{option.description}</p>
                                        </div>
                                    </div>
                                </RadioGroup.Item>
                            </div>
                        ))}
                    </RadioGroup.Root>
                </div>
            </div>

{/* Reset Confirmation Dialog */}
<AlertDialog.Root open={showResetAlert} onOpenChange={setShowResetAlert}>
        <AlertDialog.Portal>
          <AlertDialog.Overlay className="radix-dialog-overlay-motion z-20 fixed inset-0 bg-black/50" />
          <AlertDialog.Content className="radix-dialog-content-centered-motion z-30 fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[95vw] max-w-md rounded-lg p-6 bg-white shadow-xl focus:outline-none">
            <AlertDialog.Title className="text-lg font-medium text-gray-900 mb-3">
              Start Again?
            </AlertDialog.Title>
            <AlertDialog.Description className="text-sm text-gray-600 mb-5">
              Changing the workflow model will reset your current configuration. Would you like to continue?
            </AlertDialog.Description>
            <div className="flex justify-end gap-4">
              <AlertDialog.Cancel asChild>
                <button
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
                >
                  Cancel
                </button>
              </AlertDialog.Cancel>
              <AlertDialog.Action asChild>
                <button
                  onClick={() => {
                    setSelectedOption(pendingOption);
                    resetConfiguration();
                    setShowResetAlert(false);
                  }}
                  className="px-4 py-2 text-sm font-medium text-white bg-violet-600 rounded-md hover:bg-violet-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-violet-500"
                >
                  Continue
                </button>
              </AlertDialog.Action>
            </div>
          </AlertDialog.Content>
        </AlertDialog.Portal>
      </AlertDialog.Root>

            {/* Bottom Section - Module Flow Container */}
            <div className="flex flex-col space-y-6">
                <div className="flex flex-col space-y-6">
                    {/* Instructions or Congrats Message */}
  {selectedOption && (
    <div className="text-sm text-gray-600">
      {isComplete ? (
        <div className="text-center">
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            Module Configuration Complete ✅
          </h3>
          <p className="text-sm text-gray-600 max-w-md mx-auto">
            You've configured the modules to your clinical approach. You can change these later in Settings. Click "Next Step" to move on.
          </p>
        </div>
      ) : (
        <div className="text-center">
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            Step {currentStep}
          </h3>
          <p className="text-sm text-gray-600 max-w-md mx-auto">
          {getStepInstructions()}
          </p>
        </div>
      )}
    </div>
  )}

                {/* Module Flow Container */}
                {selectedOption && (
                    <div className="relative p-6 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300 min-h-[200px] flex items-center justify-center">
                    {selectedOption === 'single-session' && isComplete ? (
            // Show all modules with red background for completed single session
            <div className="flex flex-nowrap gap-4 items-center justify-center">
              {modules.map((module, index) => (
                <ModuleBlock
                  key={module.id}
                  id={module.id}
                  module={module}
                  isLocked={false}
                  showConnector={index < modules.length - 1}
                  sessionStyle="1"
                  SESSION_STYLES={SESSION_STYLES}
                  isComplete={isComplete}
                />
              ))}
            </div>
          ) : currentStep === WORKFLOW_STEPS.ORDER ? (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
          modifiers={[restrictToHorizontalAxis]}
        >
          <div className="flex flex-nowrap gap-4 items-center justify-center">
            <SortableContext
              items={modules
                .filter(m => !isModuleOrderLocked(m))
                .map(m => m.id)}
              strategy={horizontalListSortingStrategy}
            >
              {modules.map((module, index) => (
                <SortableModuleBlock
                  key={module.id}
                  module={module}
                  isLocked={isModuleOrderLocked(module)}
                  showConnector={index < modules.length - 1}
                  isDragging={activeId === module.id}
                  SESSION_STYLES={SESSION_STYLES}
                />
              ))}
            </SortableContext>
          </div>
          <DragOverlay adjustScale={true}>
            {activeId ? (
              <ModuleBlock
                module={modules.find(m => m.id === activeId)}
                isDragging={true}
                showConnector={false}
                SESSION_STYLES={SESSION_STYLES}
                isComplete={isComplete}
              />
            ) : null}
          </DragOverlay>
        </DndContext>
      ) : (
                            <div className="relative">
                                {/* Selection border container */}
                                {selectedModules.length > 0 && (
                                    <div className={`
                                        absolute -m-0.5 border-2 border-dashed rounded-2xl
                                        ${SESSION_STYLES[currentStep].border}
                                        ${SESSION_STYLES[currentStep].bg}
                                        `}
                                        style={{
                                            left: `${Math.min(...selectedModules.map(id =>
                                                document.getElementById(id)?.offsetLeft || 0
                                            )) - 8}px`,
                                            right: `${document.getElementById('container-width')?.offsetWidth -
                                                Math.max(...selectedModules.map(id =>
                                                    (document.getElementById(id)?.offsetLeft || 0) +
                                                    (document.getElementById(id)?.offsetWidth || 0)
                                                )) - 8}px`,
                                            top: '-8px',
                                            bottom: '-8px',
                                            zIndex: 0
                                        }}
                                    />
                                )}

                                <div className="flex flex-nowrap gap-4 items-center" id="container-width">
                                    {modules.map((module, index) => (
                                        <ModuleBlock
                                            key={module.id}
                                            id={module.id}
                                            module={module}
                                            isLocked={false}
                                            showConnector={index < modules.length - 1}
                                            isSelected={selectedModules.includes(module.id)}
                                            onClick={() => handleModuleClick(module.id)}
                                            sessionStyle={Object.entries(sessionGroups).find(
                                                ([_, ids]) => ids.includes(module.id)
                                            )?.[0]}
                                            SESSION_STYLES={SESSION_STYLES}
                                            isComplete={isComplete}
                                        />
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
            )}
            </div>
            
             {/* Confirm Step Button */}
             {selectedOption && !isComplete && (
          <div className="flex justify-end">
            <button
              onClick={handleConfirmStep}
              className="px-4 py-2 text-sm font-medium text-white bg-violet-600 border border-transparent rounded-md hover:bg-violet-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-violet-500"
            >
              {selectedOption === 'single-session' ? 'Confirm Order' : 
                currentStep === WORKFLOW_STEPS.ORDER ? 'Confirm Order' : 'Confirm Selection'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}